import React, { useEffect, useState, useCallback } from 'react';
import {
  Shield, Crown, Users, Clock, Globe, Brain, Info, Sparkles,
  ChevronDown, ChevronUp, Save, Loader2, Eye, EyeOff,
  Activity, Hash, Tag, AlertTriangle, CheckCircle, Lightbulb,
  ShieldAlert, X, Edit2, Trash2, Languages, Plus,
  FileText, Cpu, Wrench, Search, ChevronRight,
} from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  userAgentApi, memoriesApi,
  ExpertiseCard, AgentIdentity, LearningControls, OrgHierarchyMember, AgentMemory,
  ToolCatalogEntry, UserOrchestratorConfig,
} from '@/services/api/agentApi';
import { useChatStore } from '@/stores/agents/chat-store';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const AUTHORITY_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  owner: { bg: 'bg-amber-500/10', text: 'text-amber-700', border: 'border-amber-500/30' },
  manager: { bg: 'bg-blue-500/10', text: 'text-blue-700', border: 'border-blue-500/30' },
  member: { bg: 'bg-secondary', text: 'text-muted-foreground', border: 'border-border' },
};

const LANGUAGE_OPTIONS = [
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
  { code: 'fr', label: 'Français' },
  { code: 'pt', label: 'Português' },
  { code: 'de', label: 'Deutsch' },
  { code: 'zh', label: '中文' },
  { code: 'ja', label: '日本語' },
  { code: 'ko', label: '한국어' },
];

function timeAgo(date: string): string {
  const diff = Date.now() - new Date(date).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function AuthorityBadge({ level }: { level: string }) {
  const colors = AUTHORITY_COLORS[level] || AUTHORITY_COLORS.member;
  const Icon = level === 'owner' ? Crown : level === 'manager' ? Shield : Users;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${colors.bg} ${colors.text}`}>
      <Icon className="h-3.5 w-3.5" />
      {level.charAt(0).toUpperCase() + level.slice(1)}
    </span>
  );
}

function InfoTip({ text, children }: { text?: string; children?: React.ReactNode }) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Info className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
        </TooltipTrigger>
        <TooltipContent side="top">
          <p className="text-xs max-w-[220px]">{children || text}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// ─── Expertise Card Tab ──────────────────────────────────────────────────────

function ExpertiseCardTab({
  card,
  isOwner,
  saving,
  onSave,
}: {
  card: ExpertiseCard;
  isOwner: boolean;
  saving: boolean;
  onSave: (updates: Partial<ExpertiseCard>) => void;
}) {
  const [editDomains, setEditDomains] = useState(card.domains);
  const [editAvailability, setEditAvailability] = useState(card.availability);
  const [editRouting, setEditRouting] = useState(card.routing);
  const [editLanguage, setEditLanguage] = useState(card.preferred_language || 'en');
  const [newDomain, setNewDomain] = useState('');
  const [dirty, setDirty] = useState(false);

  const handleAddDomain = () => {
    if (!newDomain.trim()) return;
    setEditDomains([...editDomains, { name: newDomain.trim(), confidence: 0.5 }]);
    setNewDomain('');
    setDirty(true);
  };

  const handleRemoveDomain = (idx: number) => {
    setEditDomains(editDomains.filter((_, i) => i !== idx));
    setDirty(true);
  };

  const handleDomainConfidence = (idx: number, confidence: number) => {
    const updated = [...editDomains];
    updated[idx] = { ...updated[idx], confidence };
    setEditDomains(updated);
    setDirty(true);
  };

  const handleSave = () => {
    onSave({
      domains: editDomains,
      availability: editAvailability,
      routing: editRouting,
      preferred_language: editLanguage,
    });
    setDirty(false);
  };

  return (
    <div className="space-y-6">
      <p className="text-xs text-muted-foreground">
        This is what other agents and the expertise network can see about you.
      </p>

      {/* Authority + Language */}
      <div className="flex items-center gap-4">
        <div>
          <label className="text-xs text-muted-foreground flex items-center gap-1 mb-1">
            Authority Level
            <InfoTip text="Your role in the organization. Owners and managers can change authority levels in the Team Hierarchy tab." />
          </label>
          <AuthorityBadge level={card.authority_level} />
        </div>
        <div>
          <label className="text-xs text-muted-foreground flex items-center gap-1 mb-1">
            <Languages className="h-3 w-3" />
            Language
            <InfoTip text="Your preferred language for agent responses and notifications." />
          </label>
          <select
            className="text-sm border border-border rounded px-2 py-1 bg-background"
            value={editLanguage}
            onChange={(e) => { setEditLanguage(e.target.value); setDirty(true); }}
          >
            {LANGUAGE_OPTIONS.map(l => (
              <option key={l.code} value={l.code}>{l.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Domains */}
      <div>
        <div className="flex items-center gap-1.5 mb-2">
          <h4 className="text-sm font-medium">Expertise Domains</h4>
          <InfoTip text="Set your confidence level for each domain. Your agent uses this to route questions and weight your expertise." />
        </div>
        <div className="space-y-2">
          {editDomains.map((domain, idx) => (
            <div key={idx} className="flex items-center gap-3 p-2 bg-muted/30 rounded-lg">
              <span className="text-sm flex-1">{domain.name}</span>
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={domain.confidence}
                onChange={(e) => handleDomainConfidence(idx, parseFloat(e.target.value))}
                className="w-24"
              />
              <span className="text-xs w-10 text-right font-mono">
                {(domain.confidence * 100).toFixed(0)}%
              </span>
              <button
                onClick={() => handleRemoveDomain(idx)}
                className="p-1 hover:bg-accent rounded text-muted-foreground hover:text-foreground"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-2 mt-2">
          <input
            type="text"
            placeholder="Add domain..."
            value={newDomain}
            onChange={(e) => setNewDomain(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAddDomain()}
            className="flex-1 px-3 py-1.5 text-sm border border-border rounded bg-background focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
          <Button size="sm" variant="outline" onClick={handleAddDomain} disabled={!newDomain.trim()}>
            Add
          </Button>
        </div>
      </div>

      {/* Availability */}
      <div>
        <div className="flex items-center gap-1.5 mb-2">
          <h4 className="text-sm font-medium">Availability</h4>
          <InfoTip text="Controls whether your agent can contact you outside your normal working hours for urgent matters." />
        </div>
        <label className="flex items-center gap-2 text-sm">
          <Switch
            checked={editAvailability.urgentOK}
            onCheckedChange={(checked) => {
              setEditAvailability({ ...editAvailability, urgentOK: checked });
              setDirty(true);
            }}
          />
          Available for urgent queries outside normal hours
        </label>
      </div>

      {/* Routing */}
      <div>
        <div className="flex items-center gap-1.5 mb-2">
          <h4 className="text-sm font-medium">Routing Preferences</h4>
          <InfoTip text="Topics you want routed to you (accept) or away from you (decline). Helps the agent community direct questions to the right person." />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Accept topics</label>
            <div className="flex flex-wrap gap-1">
              {editRouting.accept.map((topic, i) => (
                <span key={i} className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600">
                  {topic}
                  <button onClick={() => {
                    setEditRouting({ ...editRouting, accept: editRouting.accept.filter((_, j) => j !== i) });
                    setDirty(true);
                  }}>
                    <X className="h-2.5 w-2.5" />
                  </button>
                </span>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Decline topics</label>
            <div className="flex flex-wrap gap-1">
              {editRouting.decline.map((topic, i) => (
                <span key={i} className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-red-500/10 text-red-600">
                  {topic}
                  <button onClick={() => {
                    setEditRouting({ ...editRouting, decline: editRouting.decline.filter((_, j) => j !== i) });
                    setDirty(true);
                  }}>
                    <X className="h-2.5 w-2.5" />
                  </button>
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Reports to */}
      {card.reports_to && (
        <div>
          <label className="text-xs text-muted-foreground block mb-1">Reports To</label>
          <p className="text-sm font-mono">{card.reports_to}</p>
        </div>
      )}

      {/* Save */}
      {dirty && (
        <Button onClick={handleSave} disabled={saving} className="bg-primary hover:bg-primary/90">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
          Save Changes
        </Button>
      )}
    </div>
  );
}

// ─── Private Knowledge Tab ───────────────────────────────────────────────────

function PrivateKnowledgeTab() {
  const [memories, setMemories] = useState<AgentMemory[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [correcting, setCorrecting] = useState<string | null>(null);
  const [correctionText, setCorrectionText] = useState('');
  const [deleting, setDeleting] = useState<string | null>(null);

  const fetchPersonalMemories = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = { limit: '50' };
      if (filter) params.aspects = filter;
      const res = await memoriesApi.list(params);
      setMemories(res.data || []);
    } catch { /* silent */ }
    setLoading(false);
  }, [filter]);

  useEffect(() => { fetchPersonalMemories(); }, [fetchPersonalMemories]);

  const handleCorrect = async (memoryId: string) => {
    if (!correctionText.trim()) return;
    try {
      await memoriesApi.submitFeedback({
        thread_id: 'profile-correction',
        message_id: memoryId,
        signal: 'negative',
        correction_text: correctionText,
        memory_ids: [memoryId],
      });
      setCorrecting(null);
      setCorrectionText('');
      fetchPersonalMemories();
    } catch { /* silent */ }
  };

  const handleDelete = async (memoryId: string) => {
    try {
      await memoriesApi.delete(memoryId);
      setDeleting(null);
      fetchPersonalMemories();
    } catch { /* silent */ }
  };

  const ASPECT_OPTIONS = [
    { value: '', label: 'All' },
    { value: 'user-expertise', label: 'Expertise' },
    { value: 'user-communication', label: 'Communication' },
    { value: 'user-availability', label: 'Availability' },
    { value: 'user-relationships', label: 'Relationships' },
    { value: 'user-interests', label: 'Interests' },
    { value: 'user-interaction-patterns', label: 'Patterns' },
    { value: 'user-corrections', label: 'Corrections' },
  ];

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'correction': return ShieldAlert;
      case 'directive': return ShieldAlert;
      case 'hypothesis': return Lightbulb;
      default: return CheckCircle;
    }
  };

  const getTypeBadgeClass = (type: string) => {
    switch (type) {
      case 'correction': return 'bg-orange-500/10 text-orange-600';
      case 'directive': return 'bg-red-500/10 text-red-600';
      case 'hypothesis': return 'bg-amber-500/10 text-amber-600';
      default: return 'bg-emerald-500/10 text-emerald-600';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-xs text-muted-foreground">
        These are the memories your agent has collected about you. You can correct or delete any of them.
      </p>

      {/* Filter */}
      <div className="flex items-center gap-2">
        <label className="text-xs text-muted-foreground">Filter:</label>
        <select
          className="text-sm border border-border rounded px-2 py-1 bg-background"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        >
          {ASPECT_OPTIONS.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      {memories.length === 0 ? (
        <div className="text-center py-8">
          <Brain className="h-10 w-10 mx-auto text-muted-foreground/40 mb-2" />
          <p className="text-sm text-muted-foreground">No personal memories yet</p>
        </div>
      ) : (
        <div className="space-y-3">
          {memories.map((memory) => {
            const TypeIcon = getTypeIcon(memory.type);
            const isCorrectingThis = correcting === memory._id;
            const isDeletingThis = deleting === memory._id;

            return (
              <div key={memory._id} className="border border-border rounded-lg p-3 space-y-2">
                {/* Header */}
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm flex-1">{memory.text}</p>
                  <div className="flex items-center gap-1 shrink-0">
                    <span className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${getTypeBadgeClass(memory.type)}`}>
                      <TypeIcon className="h-3 w-3" />
                      {memory.type}
                    </span>
                    <InfoTip><strong>Facts</strong> are confirmed knowledge, <strong>hypotheses</strong> are unverified, <strong>corrections</strong> override previous knowledge, and <strong>directives</strong> are your explicit instructions.</InfoTip>
                  </div>
                </div>

                {/* Weight bar */}
                <div className="flex items-center gap-2">
                  <InfoTip>How strongly this memory <strong>influences</strong> your agent. Higher weight means more <strong>impact</strong>. Weight <strong>decays</strong> over time if not reinforced.</InfoTip>
                  <div className="flex-1 h-1.5 bg-secondary rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full"
                      style={{
                        width: `${memory.weight * 100}%`,
                        backgroundColor: memory.weight > 0.7 ? '#22c55e' : memory.weight >= 0.3 ? '#f59e0b' : '#ef4444',
                      }}
                    />
                  </div>
                  <span className="text-[10px] text-muted-foreground w-8 text-right">
                    {(memory.weight * 100).toFixed(0)}%
                  </span>
                </div>

                {/* Aspects */}
                <div className="flex flex-wrap gap-1">
                  {memory.aspects.map((aspect, i) => (
                    <span key={i} className="flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded bg-secondary text-muted-foreground">
                      <Hash className="h-2.5 w-2.5" />
                      {aspect}
                    </span>
                  ))}
                  {memory.origin && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-secondary text-muted-foreground">
                      {memory.origin}
                    </span>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 pt-1">
                  <button
                    onClick={() => { setCorrecting(isCorrectingThis ? null : memory._id); setCorrectionText(''); }}
                    className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700"
                  >
                    <Edit2 className="h-3 w-3" />
                    {isCorrectingThis ? 'Cancel' : 'Correct'}
                  </button>
                  <button
                    onClick={() => setDeleting(isDeletingThis ? null : memory._id)}
                    className="flex items-center gap-1 text-xs text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="h-3 w-3" />
                    {isDeletingThis ? 'Cancel' : 'Delete'}
                  </button>
                  <span className="ml-auto text-[10px] text-muted-foreground">
                    {timeAgo(memory.created_at)}
                  </span>
                </div>

                {/* Correction input */}
                {isCorrectingThis && (
                  <div className="flex items-center gap-2 pt-1">
                    <input
                      type="text"
                      placeholder="Enter the correct information..."
                      value={correctionText}
                      onChange={(e) => setCorrectionText(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleCorrect(memory._id)}
                      className="flex-1 px-3 py-1.5 text-sm border border-border rounded bg-background focus:outline-none focus:ring-2 focus:ring-primary/30"
                      autoFocus
                    />
                    <Button size="sm" onClick={() => handleCorrect(memory._id)} disabled={!correctionText.trim()}>
                      Submit
                    </Button>
                  </div>
                )}

                {/* Delete confirmation */}
                {isDeletingThis && (
                  <div className="flex items-center gap-2 p-2 bg-red-500/5 border border-red-500/20 rounded">
                    <AlertTriangle className="h-4 w-4 text-red-500 shrink-0" />
                    <span className="text-xs text-red-600 flex-1">Permanently delete this memory?</span>
                    <Button size="sm" variant="destructive" onClick={() => handleDelete(memory._id)}>
                      Delete
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Agent Identity Tab ──────────────────────────────────────────────────────

function AgentIdentityTab({
  card,
  onSave,
  saving,
}: {
  card: ExpertiseCard;
  onSave: (updates: Partial<ExpertiseCard>) => void;
  saving: boolean;
}) {
  const identity = card.agent_identity || { name: '', aliases: [], personality: '', enabled: true, named_by: 'user' as const };
  const [name, setName] = useState(identity.name);
  const [aliases, setAliases] = useState<string[]>(identity.aliases || []);
  const [personality, setPersonality] = useState(identity.personality);
  const [newAlias, setNewAlias] = useState('');
  const [dirty, setDirty] = useState(false);
  const [duplicateWarning, setDuplicateWarning] = useState('');

  // Debounced duplicate check
  useEffect(() => {
    if (!name.trim()) { setDuplicateWarning(''); return; }
    const timer = setTimeout(async () => {
      try {
        const res = await userAgentApi.checkAgentName(name);
        const others = res.data?.duplicates || [];
        if (others.length > 0) {
          setDuplicateWarning(
            `${others.map(d => d.userDisplayName || d.userId).join(', ')} also ${others.length === 1 ? 'has' : 'have'} an agent named "${name}".`
          );
        } else {
          setDuplicateWarning('');
        }
      } catch { /* silent */ }
    }, 500);
    return () => clearTimeout(timer);
  }, [name]);

  const handleSave = () => {
    onSave({
      agent_identity: { name, aliases, personality, enabled: identity.enabled, named_by: identity.named_by },
    });
    // Update the chat store's agentName so the UI reflects the change immediately
    useChatStore.getState().agentName !== name && useChatStore.setState({ agentName: name || null });
    setDirty(false);
  };

  const handleAddAlias = () => {
    const trimmed = newAlias.trim();
    if (trimmed && !aliases.includes(trimmed)) {
      setAliases([...aliases, trimmed]);
      setNewAlias('');
      setDirty(true);
    }
  };

  const handleRemoveAlias = (alias: string) => {
    setAliases(aliases.filter(a => a !== alias));
    setDirty(true);
  };

  return (
    <div className="space-y-5">
      <p className="text-xs text-muted-foreground">
        Give your personal agent a name and personality. This shapes how it introduces itself and communicates with you.
      </p>

      {/* Agent Name */}
      <div className="space-y-2">
        <label className="text-sm font-medium">Agent Name</label>
        <input
          type="text"
          value={name}
          onChange={(e) => { setName(e.target.value); setDirty(true); }}
          placeholder="e.g., Joshua, Atlas, Nova..."
          className="w-full px-3 py-2 text-sm border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary/30"
        />
        {duplicateWarning && (
          <div className="flex items-start gap-2 p-2 bg-amber-500/5 border border-amber-500/20 rounded-md">
            <AlertTriangle className="h-3.5 w-3.5 text-amber-500 shrink-0 mt-0.5" />
            <p className="text-xs text-amber-600">{duplicateWarning}</p>
          </div>
        )}
        {identity.named_by === 'ai' && identity.name && (
          <p className="text-xs text-muted-foreground">This name was chosen by AI. You can change it anytime.</p>
        )}
      </div>

      {/* Aliases */}
      <div className="space-y-2">
        <label className="text-sm font-medium">Nicknames / Aliases</label>
        <p className="text-xs text-muted-foreground">Your agent will also respond to these names.</p>
        <div className="flex flex-wrap gap-1.5">
          {aliases.map((alias) => (
            <span
              key={alias}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-secondary border border-border"
            >
              {alias}
              <button
                onClick={() => handleRemoveAlias(alias)}
                className="hover:text-red-500 transition-colors"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <input
            type="text"
            value={newAlias}
            onChange={(e) => setNewAlias(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddAlias())}
            placeholder="Add a nickname..."
            className="flex-1 px-3 py-1.5 text-sm border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
          <Button size="sm" variant="outline" onClick={handleAddAlias} disabled={!newAlias.trim()}>
            <Plus className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {/* Personality */}
      <div className="space-y-2">
        <label className="text-sm font-medium">Personality</label>
        <p className="text-xs text-muted-foreground">Describe how your agent should communicate. Leave blank for neutral tone.</p>
        <textarea
          value={personality}
          onChange={(e) => { setPersonality(e.target.value); setDirty(true); }}
          placeholder="e.g., Lighthearted and funny for casual conversation, but professional and focused when working on serious tasks..."
          rows={3}
          className="w-full px-3 py-2 text-sm border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
        />
      </div>

      {/* Save button */}
      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={saving || !dirty}
          size="sm"
          className="gap-1.5 bg-primary hover:bg-primary/90"
        >
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
          Save Identity
        </Button>
      </div>
    </div>
  );
}

// ─── Learning Controls Tab ───────────────────────────────────────────────────

function LearningControlsTab() {
  const [controls, setControls] = useState<LearningControls | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    userAgentApi.getLearningControls()
      .then(r => setControls(r.data?.controls || null))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleToggle = async (key: keyof LearningControls, value: boolean) => {
    if (!controls) return;
    const updated = { ...controls, [key]: value };
    setControls(updated);
    setSaving(true);
    try {
      await userAgentApi.setLearningControls({ [key]: value });
    } catch {
      setControls({ ...controls }); // revert
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!controls) {
    return <p className="text-sm text-muted-foreground py-4">Learning controls not available.</p>;
  }

  const CONTROL_ITEMS: Array<{ key: keyof LearningControls; label: string; description: string; tooltip: React.ReactNode }> = [
    { key: 'observeTiming', label: 'Observe Response Timing', description: 'Learn when you\'re most active to optimize notification delivery.', tooltip: <>When <strong>enabled</strong>, your agent tracks when you respond to messages to learn your active hours and optimize when it sends notifications.</> },
    { key: 'trackTopics', label: 'Track Topic Engagement', description: 'Detect which topics you engage with most to build your expertise profile.', tooltip: <>When <strong>enabled</strong>, your agent notes which topics you ask about or engage with most, helping it route relevant questions to you.</> },
    { key: 'learnFromCorrections', label: 'Learn from Corrections', description: 'Use your corrections to improve future responses and memory quality.', tooltip: <>When <strong>enabled</strong>, corrections you make to memories are used to refine future responses. <strong>Disabling</strong> stops the agent from learning from feedback.</> },
    { key: 'askProactiveQuestions', label: 'Ask Proactive Questions', description: 'Occasionally ask clarifying questions to deepen understanding of your preferences.', tooltip: <>When <strong>enabled</strong>, your agent may occasionally ask you questions to better understand your preferences and fill knowledge gaps.</> },
    { key: 'crossAgentSignals', label: 'Cross-Agent Learning', description: 'Allow other agents\' observations to inform your profile (e.g., data patterns suggest expertise).', tooltip: <>When <strong>enabled</strong>, other agents in your organization can contribute observations about your expertise based on your interactions with them.</> },
    { key: 'usePersonality', label: 'Agent Personality', description: 'Allow your agent to use its assigned name and personality. When disabled, your agent behaves generically.', tooltip: <>When <strong>enabled</strong>, your agent uses its assigned name and personality traits in conversations. <strong>Disabling</strong> makes it behave as a generic assistant.</> },
  ];

  return (
    <div className="space-y-4">
      <p className="text-xs text-muted-foreground">
        Control what your personal agent learns about you. Disabling a channel creates a directive that prevents that type of observation.
      </p>

      <div className="space-y-3">
        {CONTROL_ITEMS.map(({ key, label, description, tooltip }) => (
          <div key={key} className="flex items-start gap-3 p-3 bg-muted/30 rounded-lg border border-border">
            <Switch
              checked={controls[key]}
              onCheckedChange={(checked) => handleToggle(key, checked)}
              disabled={saving}
            />
            <div className="flex-1">
              <div className="flex items-center gap-1.5">
                <p className="text-sm font-medium">{label}</p>
                <InfoTip>{tooltip}</InfoTip>
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
            </div>
            {controls[key] ? (
              <Eye className="h-4 w-4 text-emerald-500 mt-0.5" />
            ) : (
              <EyeOff className="h-4 w-4 text-muted-foreground mt-0.5" />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Activity Log Tab ────────────────────────────────────────────────────────

function ActivityLogTab() {
  const [activities, setActivities] = useState<Array<{ id: string; type: string; text: string; aspects: string[]; weight: number; createdAt: string }>>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    userAgentApi.getActivityLog(50)
      .then(r => setActivities(r.data?.activities || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (activities.length === 0) {
    return (
      <div className="text-center py-8">
        <Activity className="h-10 w-10 mx-auto text-muted-foreground/40 mb-2" />
        <p className="text-sm text-muted-foreground">No agent activity yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-xs text-muted-foreground">
        Recent actions your personal agent has taken — observations, questions asked, and memory changes.
      </p>

      <div className="space-y-2">
        {activities.map((act) => (
          <div key={act.id} className="flex items-start gap-3 p-2 border-b border-border/50">
            <div className="flex items-center gap-1 shrink-0">
              <span className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${
                act.type === 'hypothesis' ? 'bg-amber-500/10 text-amber-600'
                  : act.type === 'directive' ? 'bg-red-500/10 text-red-600'
                  : act.type === 'correction' ? 'bg-orange-500/10 text-orange-600'
                  : 'bg-emerald-500/10 text-emerald-600'
              }`}>
                {act.type}
              </span>
              <InfoTip><strong>Facts</strong> are confirmed, <strong>hypotheses</strong> are unverified, <strong>corrections</strong> override previous knowledge, <strong>directives</strong> are your explicit instructions.</InfoTip>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm truncate">{act.text}</p>
              <div className="flex items-center gap-2 mt-0.5">
                {act.aspects.slice(0, 3).map((a, i) => (
                  <span key={i} className="text-[10px] px-1 py-0.5 rounded bg-secondary text-muted-foreground">
                    {a}
                  </span>
                ))}
                {act.aspects.length > 0 && (
                  <InfoTip text="Categories this activity relates to, like your expertise, availability, or interaction patterns." />
                )}
              </div>
            </div>
            <span className="text-[10px] text-muted-foreground shrink-0">
              {timeAgo(act.createdAt)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Team Hierarchy Tab (owner-only) ─────────────────────────────────────────

function TeamHierarchyTab() {
  const [members, setMembers] = useState<OrgHierarchyMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);

  const fetchHierarchy = useCallback(async () => {
    setLoading(true);
    try {
      const res = await userAgentApi.getOrgHierarchy();
      setMembers(res.data || []);
    } catch { /* silent */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchHierarchy(); }, [fetchHierarchy]);

  const handleSetAuthority = async (userId: string, level: 'owner' | 'manager' | 'member') => {
    setSaving(userId);
    try {
      await userAgentApi.setAuthority(userId, level);
      fetchHierarchy();
    } catch { /* silent */ }
    setSaving(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const owners = members.filter(m => m.authorityLevel === 'owner');
  const managers = members.filter(m => m.authorityLevel === 'manager');
  const memberLevel = members.filter(m => m.authorityLevel === 'member');

  return (
    <div className="space-y-4">
      <p className="text-xs text-muted-foreground">
        Manage the business authority hierarchy. Authority determines how much weight a person's knowledge carries in the memory system.
      </p>

      {/* Org tree */}
      {[
        { label: 'Owners', items: owners, color: 'border-amber-500/30' },
        { label: 'Managers', items: managers, color: 'border-blue-500/30' },
        { label: 'Members', items: memberLevel, color: 'border-border' },
      ].map(({ label, items, color }) => (
        <div key={label}>
          <h4 className="text-xs font-medium text-muted-foreground mb-2">{label} ({items.length})</h4>
          {items.length === 0 ? (
            <p className="text-xs text-muted-foreground/60 ml-4">None</p>
          ) : (
            <div className="space-y-2">
              {items.map((member) => (
                <div key={member.userId} className={`flex items-center gap-3 p-3 rounded-lg border ${color} bg-muted/20`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{member.displayName}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <AuthorityBadge level={member.authorityLevel} />
                      {member.preferredLanguage && member.preferredLanguage !== 'en' && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-secondary text-muted-foreground">
                          <Languages className="h-2.5 w-2.5 inline mr-0.5" />
                          {member.preferredLanguage}
                        </span>
                      )}
                      {member.domains.slice(0, 3).map((d, i) => (
                        <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-secondary text-muted-foreground">
                          {d.name}
                        </span>
                      ))}
                    </div>
                  </div>
                  {/* Authority controls - can't change your own level if owner */}
                  {member.authorityLevel !== 'owner' && (
                    <select
                      className="text-xs border border-border rounded px-2 py-1 bg-background"
                      value={member.authorityLevel}
                      onChange={(e) => {
                        if (isEditableAuthorityLevel(e.target.value)) {
                          handleSetAuthority(member.userId, e.target.value);
                        }
                      }}
                      disabled={saving === member.userId}
                    >
                      <option value="member">Member</option>
                      <option value="manager">Manager</option>
                    </select>
                  )}
                  {saving === member.userId && <Loader2 className="h-4 w-4 animate-spin" />}
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Custom Instructions Tab ─────────────────────────────────────────────────

function CustomInstructionsTab() {
  const [config, setConfig] = useState<UserOrchestratorConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [systemPrompt, setSystemPrompt] = useState('');
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    userAgentApi.getOrchestratorConfig()
      .then(r => {
        const c = r.data;
        setConfig(c);
        setSystemPrompt(c?.system_prompt || '');
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await userAgentApi.updateOrchestratorConfig({ system_prompt: systemPrompt });
      setConfig(res.data);
      setDirty(false);
    } catch { /* silent */ }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!config) {
    return (
      <div className="text-center py-8">
        <FileText className="h-10 w-10 mx-auto text-muted-foreground/40 mb-2" />
        <p className="text-sm text-muted-foreground">Chat with your agent first to initialize its configuration.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-xs text-muted-foreground">
        Custom instructions shape how your agent behaves, responds, and reasons. These are prepended to every conversation.
      </p>

      <div className="space-y-2">
        <label className="text-sm font-medium">System Prompt</label>
        <textarea
          value={systemPrompt}
          onChange={(e) => { setSystemPrompt(e.target.value); setDirty(true); }}
          placeholder="e.g., Always respond in a concise, professional manner. Focus on actionable suggestions. When discussing data, include relevant metrics..."
          rows={10}
          className="w-full px-3 py-2 text-sm font-mono border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary/30 resize-y"
        />
        <div className="flex items-center justify-between">
          <span className="text-[10px] text-muted-foreground">{systemPrompt.length} characters</span>
          {dirty && (
            <Button onClick={handleSave} disabled={saving} size="sm" className="gap-1.5 bg-primary hover:bg-primary/90">
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
              Save Instructions
            </Button>
          )}
        </div>
      </div>

      {/* Knowledge Entries */}
      <KnowledgeEntriesSection config={config} onUpdate={setConfig} />
    </div>
  );
}

// ─── Knowledge Entries Section (within Custom Instructions) ──────────────────

function KnowledgeEntriesSection({
  config,
  onUpdate,
}: {
  config: UserOrchestratorConfig;
  onUpdate: (c: UserOrchestratorConfig) => void;
}) {
  const [entries, setEntries] = useState<string[]>(config.knowledge_entries || []);
  const [newEntry, setNewEntry] = useState('');
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const handleAdd = () => {
    if (!newEntry.trim()) return;
    setEntries([...entries, newEntry.trim()]);
    setNewEntry('');
    setDirty(true);
  };

  const handleRemove = (idx: number) => {
    setEntries(entries.filter((_, i) => i !== idx));
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await userAgentApi.updateOrchestratorConfig({ knowledge_entries: entries });
      onUpdate(res.data);
      setDirty(false);
    } catch { /* silent */ }
    setSaving(false);
  };

  return (
    <div className="space-y-3 pt-4 border-t border-border">
      <div className="flex items-center gap-1.5">
        <h4 className="text-sm font-medium">Permanent Knowledge</h4>
        <InfoTip text="Facts that your agent should always know. These never decay, unlike auto-generated memories." />
      </div>

      {entries.length > 0 && (
        <div className="space-y-1.5">
          {entries.map((entry, idx) => (
            <div key={idx} className="flex items-start gap-2 p-2 bg-muted/30 rounded-lg group">
              <span className="text-sm flex-1">{entry}</span>
              <button
                onClick={() => handleRemove(idx)}
                className="p-1 opacity-0 group-hover:opacity-100 hover:bg-accent rounded text-muted-foreground hover:text-foreground transition-opacity"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-2">
        <input
          type="text"
          value={newEntry}
          onChange={(e) => setNewEntry(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
          placeholder="Add a permanent knowledge entry..."
          className="flex-1 px-3 py-1.5 text-sm border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary/30"
        />
        <Button size="sm" variant="outline" onClick={handleAdd} disabled={!newEntry.trim()}>
          <Plus className="h-3.5 w-3.5" />
        </Button>
      </div>

      {dirty && (
        <Button onClick={handleSave} disabled={saving} size="sm" className="gap-1.5 bg-primary hover:bg-primary/90">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
          Save Knowledge
        </Button>
      )}
    </div>
  );
}

// ─── Model & Resources Tab ──────────────────────────────────────────────────

const MODEL_TIERS = [
  { tier: 1, label: 'Frontier', description: 'Complex reasoning, slower, higher cost' },
  { tier: 2, label: 'Capable', description: 'Balanced quality and speed' },
  { tier: 3, label: 'Fast', description: 'Quick responses, lower cost' },
];

const BUDGET_OPTIONS = [
  { value: 'low', label: 'Low', description: 'Minimal resource usage' },
  { value: 'medium', label: 'Medium', description: 'Balanced allocation' },
  { value: 'high', label: 'High', description: 'Priority processing' },
  { value: 'critical', label: 'Critical', description: 'Maximum resources' },
] as const;

type BudgetPriority = (typeof BUDGET_OPTIONS)[number]['value'];
type EditableAuthorityLevel = Exclude<OrgHierarchyMember['authorityLevel'], 'owner'>;

function isEditableAuthorityLevel(value: string): value is EditableAuthorityLevel {
  return value === 'manager' || value === 'member';
}

function ModelResourcesTab() {
  const [config, setConfig] = useState<UserOrchestratorConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modelTier, setModelTier] = useState(3);
  const [budget, setBudget] = useState<BudgetPriority>('low');
  const [maxExperts, setMaxExperts] = useState(0);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    userAgentApi.getOrchestratorConfig()
      .then(r => {
        const c = r.data;
        setConfig(c);
        if (c) {
          setModelTier(c.resource_preferences?.default_model_tier || 3);
          setBudget(c.resource_preferences?.budget_priority || 'low');
          setMaxExperts(c.resource_preferences?.max_expert_agents || 0);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await userAgentApi.updateOrchestratorConfig({
        resource_preferences: {
          default_model_tier: modelTier,
          budget_priority: budget,
          max_expert_agents: maxExperts,
        },
      });
      setConfig(res.data);
      setDirty(false);
    } catch { /* silent */ }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!config) {
    return (
      <div className="text-center py-8">
        <Cpu className="h-10 w-10 mx-auto text-muted-foreground/40 mb-2" />
        <p className="text-sm text-muted-foreground">Chat with your agent first to initialize its configuration.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <p className="text-xs text-muted-foreground">
        Control the intelligence level, resource allocation, and sub-agent limits for your personal agent.
      </p>

      {/* Model Tier */}
      <div className="space-y-3">
        <div className="flex items-center gap-1.5">
          <h4 className="text-sm font-medium">Model Tier</h4>
          <InfoTip text="Higher tiers use more capable (and expensive) AI models. Your agent auto-falls back to the next tier if the selected one is unavailable." />
        </div>
        <div className="space-y-2">
          {MODEL_TIERS.map(t => (
            <label
              key={t.tier}
              className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                modelTier === t.tier
                  ? 'border-primary/50 bg-primary/10'
                  : 'border-border hover:border-muted-foreground/30'
              }`}
            >
              <input
                type="radio"
                name="modelTier"
                value={t.tier}
                checked={modelTier === t.tier}
                onChange={() => { setModelTier(t.tier); setDirty(true); }}
                className="mt-0.5 accent-primary"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">Tier {t.tier} — {t.label}</span>
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">{t.description}</p>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Budget Priority */}
      <div className="space-y-2">
        <div className="flex items-center gap-1.5">
          <h4 className="text-sm font-medium">Budget Priority</h4>
          <InfoTip text="Determines how much of the organization's AI budget your agent can consume. Higher priority means more LLM calls and larger contexts." />
        </div>
        <div className="grid grid-cols-4 gap-2">
          {BUDGET_OPTIONS.map(o => (
            <button
              key={o.value}
              onClick={() => { setBudget(o.value); setDirty(true); }}
              className={`p-2 rounded-lg border text-center transition-colors ${
                budget === o.value
                  ? 'border-primary/50 bg-primary/10'
                  : 'border-border hover:border-muted-foreground/30'
              }`}
            >
              <p className="text-xs font-medium">{o.label}</p>
              <p className="text-[10px] text-muted-foreground mt-0.5">{o.description}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Max Expert Agents */}
      <div className="space-y-2">
        <div className="flex items-center gap-1.5">
          <h4 className="text-sm font-medium">Max Expert Agents</h4>
          <InfoTip text="Maximum number of sub-agents your agent can spawn for complex tasks. More agents = faster but costlier." />
        </div>
        <div className="flex items-center gap-3">
          <input
            type="range"
            min="0"
            max="10"
            value={maxExperts}
            onChange={(e) => { setMaxExperts(parseInt(e.target.value)); setDirty(true); }}
            className="flex-1"
          />
          <span className="text-sm font-mono w-8 text-right">{maxExperts}</span>
        </div>
        <p className="text-[10px] text-muted-foreground">
          {maxExperts === 0 ? 'No sub-agents — your agent handles everything alone' : `Up to ${maxExperts} sub-agent${maxExperts > 1 ? 's' : ''} can be spawned per task`}
        </p>
      </div>

      {/* Save */}
      {dirty && (
        <Button onClick={handleSave} disabled={saving} size="sm" className="gap-1.5 bg-primary hover:bg-primary/90">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
          Save Resources
        </Button>
      )}
    </div>
  );
}

// ─── Tools Catalog Tab ──────────────────────────────────────────────────────

const TIER_BADGE: Record<string, { label: string; className: string }> = {
  READ_ONLY: { label: 'Read Only', className: 'bg-emerald-500/20 text-emerald-400' },
  REVERSIBLE: { label: 'Reversible', className: 'bg-amber-500/20 text-amber-400' },
  IRREVERSIBLE: { label: 'Irreversible', className: 'bg-red-500/20 text-red-400' },
  DERIVED: { label: 'Derived', className: 'bg-muted text-muted-foreground' },
};

function ToolsCatalogTab() {
  const [tools, setTools] = useState<ToolCatalogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [expandedTool, setExpandedTool] = useState<string | null>(null);

  useEffect(() => {
    userAgentApi.getToolsCatalog()
      .then(r => setTools(r.data?.tools || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (tools.length === 0) {
    return (
      <div className="text-center py-8">
        <Wrench className="h-10 w-10 mx-auto text-muted-foreground/40 mb-2" />
        <p className="text-sm text-muted-foreground">No tools available yet.</p>
      </div>
    );
  }

  const q = searchInput.toLowerCase();
  const filtered = q
    ? tools.filter(t =>
        t.name.toLowerCase().includes(q) ||
        t.description.toLowerCase().includes(q) ||
        t.category.toLowerCase().includes(q)
      )
    : tools;

  // Group by category
  const categories = new Map<string, ToolCatalogEntry[]>();
  for (const tool of filtered) {
    const cat = tool.category || 'Other';
    const categoryTools = categories.get(cat);
    if (categoryTools) {
      categoryTools.push(tool);
    } else {
      categories.set(cat, [tool]);
    }
  }

  return (
    <div className="space-y-4">
      <p className="text-xs text-muted-foreground">
        Tools your agent can use to take actions. Safety tiers determine whether confirmation is needed before execution.
      </p>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
        <input
          type="text"
          placeholder="Search tools..."
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          className="w-full pl-9 pr-8 py-1.5 text-sm border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary/30"
        />
        {searchInput && (
          <button
            onClick={() => setSearchInput('')}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-muted-foreground hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      <p className="text-xs text-muted-foreground">{filtered.length} tool{filtered.length !== 1 ? 's' : ''} available</p>

      {/* Tool list grouped by category */}
      {Array.from(categories.entries()).map(([category, categoryTools]) => (
        <div key={category}>
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{category}</span>
            <span className="text-[10px] text-muted-foreground">({categoryTools.length})</span>
          </div>
          <div className="space-y-1.5">
            {categoryTools.map(tool => {
              const tierBadge = TIER_BADGE[tool.tier] || TIER_BADGE.DERIVED;
              const isExpanded = expandedTool === tool.name;

              return (
                <div key={tool.name} className="border border-border rounded-lg overflow-hidden">
                  <button
                    onClick={() => setExpandedTool(isExpanded ? null : tool.name)}
                    className="w-full flex items-start gap-3 p-3 text-left hover:bg-muted/30 transition-colors"
                  >
                    <Wrench className="h-3.5 w-3.5 text-muted-foreground mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium">{tool.name}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${tierBadge.className}`}>
                          {tierBadge.label}
                        </span>
                        {tool.idempotent && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-400">
                            Idempotent
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{tool.description}</p>
                    </div>
                    <ChevronRight className={`h-4 w-4 text-muted-foreground shrink-0 mt-0.5 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                  </button>

                  {isExpanded && (
                    <div className="px-3 pb-3 pt-0 border-t border-border bg-muted/10 space-y-2">
                      <p className="text-xs text-muted-foreground">{tool.description}</p>

                      {/* Input params */}
                      {tool.input_params.length > 0 && (
                        <div>
                          <p className="text-[10px] font-medium text-muted-foreground mb-1">Parameters</p>
                          <div className="space-y-1">
                            {tool.input_params.map(p => (
                              <div key={p.name} className="flex items-center gap-2 text-xs">
                                <code className="font-mono text-[11px] px-1 py-0.5 bg-secondary rounded">{p.name}</code>
                                <span className="text-muted-foreground">{p.type}</span>
                                {p.required && <span className="text-[10px] text-red-500">required</span>}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Capability tags */}
                      {tool.capability_tags.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {tool.capability_tags.map(tag => (
                            <span key={tag} className="text-[10px] px-1.5 py-0.5 rounded bg-secondary text-muted-foreground">
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}

                      {/* Side effects */}
                      {tool.side_effects.length > 0 && (
                        <div>
                          <p className="text-[10px] font-medium text-muted-foreground mb-1">Side Effects</p>
                          <div className="flex flex-wrap gap-1">
                            {tool.side_effects.map(se => (
                              <span key={se} className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400">
                                {se}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────

export function UserAgentProfile({ compact }: { compact?: boolean } = {}) {
  const [card, setCard] = useState<ExpertiseCard | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isOwner, setIsOwner] = useState(false);

  useEffect(() => {
    Promise.all([
      userAgentApi.getExpertiseCard(),
      userAgentApi.getStatus(),
    ]).then(([cardRes, statusRes]) => {
      const c = cardRes.data;
      setCard(c || null);
      setIsOwner(c?.authority_level === 'owner');
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleSaveCard = async (updates: Partial<ExpertiseCard>) => {
    setSaving(true);
    try {
      const res = await userAgentApi.updateExpertiseCard(updates);
      setCard(res.data);
    } catch { /* silent */ }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const defaultCard: ExpertiseCard = card || {
    user_id: '',
    organization_id: '',
    display_name: 'Your Agent',
    authority_level: 'member',
    preferred_language: 'en',
    domains: [],
    availability: { windows: [], urgentOK: false },
    routing: { accept: [], decline: [] },
    response: { avgTimeMinutes: 0, preferredChannel: 'chat' },
    agent_identity: { name: '', aliases: [], personality: '', enabled: true, named_by: 'user' as const },
  };

  const contentHeight = compact ? 'h-[calc(75vh-100px)]' : 'h-[60vh]';

  return (
    <div>
      {/* Header - hidden in compact mode since parent provides it */}
      {!compact && (
        <div className="flex items-center gap-3 px-4 pt-4 pb-2">
          <div className="p-1.5 rounded-lg bg-primary/10">
            <Brain className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-card-foreground">My agent profile</h2>
            <p className="text-[11px] text-muted-foreground">
              View and manage what your personal agent knows about you
            </p>
          </div>
        </div>
      )}

      <Tabs defaultValue="identity">
        {/* Tab bar */}
        <div className="px-4 pb-3">
          <TabsList className="w-full h-9">
            <TabsTrigger value="identity" className="text-xs flex-1">Identity</TabsTrigger>
            <TabsTrigger value="instructions" className="text-xs flex-1">Instructions</TabsTrigger>
            <TabsTrigger value="model" className="text-xs flex-1">Model</TabsTrigger>
            <TabsTrigger value="tools" className="text-xs flex-1">Tools</TabsTrigger>
            <TabsTrigger value="expertise" className="text-xs flex-1">Expertise</TabsTrigger>
            <TabsTrigger value="knowledge" className="text-xs flex-1">Knowledge</TabsTrigger>
            <TabsTrigger value="learning" className="text-xs flex-1">Learning</TabsTrigger>
            <TabsTrigger value="activity" className="text-xs flex-1">Activity</TabsTrigger>
            {isOwner && <TabsTrigger value="team" className="text-xs flex-1">Team</TabsTrigger>}
          </TabsList>
        </div>

        <TabsContent value="identity" className={`data-[state=active]:${contentHeight} data-[state=inactive]:h-0 overflow-y-auto mt-0 px-4 py-4`}>
          <AgentIdentityTab card={defaultCard} onSave={handleSaveCard} saving={saving} />
        </TabsContent>

        <TabsContent value="instructions" className={`data-[state=active]:${contentHeight} data-[state=inactive]:h-0 overflow-y-auto mt-0 px-4 py-4`}>
          <CustomInstructionsTab />
        </TabsContent>

        <TabsContent value="model" className={`data-[state=active]:${contentHeight} data-[state=inactive]:h-0 overflow-y-auto mt-0 px-4 py-4`}>
          <ModelResourcesTab />
        </TabsContent>

        <TabsContent value="tools" className={`data-[state=active]:${contentHeight} data-[state=inactive]:h-0 overflow-y-auto mt-0 px-4 py-4`}>
          <ToolsCatalogTab />
        </TabsContent>

        <TabsContent value="expertise" className={`data-[state=active]:${contentHeight} data-[state=inactive]:h-0 overflow-y-auto mt-0 px-4 py-4`}>
          <ExpertiseCardTab
            card={defaultCard}
            isOwner={isOwner}
            saving={saving}
            onSave={handleSaveCard}
          />
        </TabsContent>

        <TabsContent value="knowledge" className={`data-[state=active]:${contentHeight} data-[state=inactive]:h-0 overflow-y-auto mt-0 px-4 py-4`}>
          <PrivateKnowledgeTab />
        </TabsContent>

        <TabsContent value="learning" className={`data-[state=active]:${contentHeight} data-[state=inactive]:h-0 overflow-y-auto mt-0 px-4 py-4`}>
          <LearningControlsTab />
        </TabsContent>

        <TabsContent value="activity" className={`data-[state=active]:${contentHeight} data-[state=inactive]:h-0 overflow-y-auto mt-0 px-4 py-4`}>
          <ActivityLogTab />
        </TabsContent>

        {isOwner && (
          <TabsContent value="team" className={`data-[state=active]:${contentHeight} data-[state=inactive]:h-0 overflow-y-auto mt-0 px-4 py-4`}>
            <TeamHierarchyTab />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
